import { keyPartRulesDefaults } from '../s2/defaults';
import {
  HAUL_CLASSES,
  type HaulClassRuleDef,
  type KeyPartDef,
  type KeyPartRulesDef,
} from '../s2/types';
import { teamRulesDefaults } from '../teamsDefaults';
import type { EffectDef } from '../story/types';
import { flatten } from './beats';
import type { Err, Ids } from './common';
import { fmtNum, idsIn, isChance } from './s2common';
import type { Bundle } from './types';

/** `InteriorGenerator.MaxRooms`. */
const MAX_ROOMS = 16;
/** `CarryRules.BaseCapacityKg`. */
const BASE_CAPACITY_KG = 20;

/** `KeyPartSites.IsWellFormed`: clauses `key=value` or `key=*`, joined by `+`, no quotes or backslashes. */
export function isWellFormedTag(rule: string | null | undefined): boolean {
  if (!rule || !rule.trim()) return false;
  for (const raw of rule.split('+')) {
    const clause = raw.trim();
    const eq = clause.indexOf('=');
    if (eq <= 0 || eq === clause.length - 1 || clause.includes('"') || clause.includes('\\'))
      return false;
  }
  return true;
}

/** `CarryWeight.ToGrams`: kg → grams, half away from zero. */
const toGrams = (kg: number) => Math.sign(kg) * Math.round(Math.abs(kg) * 1000);

/** `HaulRules.FullTeamCapacityKg`: base + the biggest backpack + member carry × (members − 1), in float maths. */
export function fullTeamCapacityKg(b: Bundle): number {
  let best = 0;
  for (const i of b.items ?? [])
    if (i?.carryBonusKg != null && i.carryBonusKg > best) best = i.carryBonusKg;
  const team =
    b.teamRules === null ? undefined : { ...teamRulesDefaults(), ...(b.teamRules ?? {}) };
  const members = Math.max(1, team?.maxMembersPerTeam ?? 1);
  const perMember = team?.memberCarryKg ?? 0;
  return Math.fround(
    Math.fround(BASE_CAPACITY_KG + Math.fround(best)) +
      Math.fround(Math.fround(perMember) * (members - 1)),
  );
}

/** `HaulRules.HardLimitGrams`. */
export const hardLimitGrams = (capacityKg: number, pct: number) =>
  capacityKg <= 0 ? 0 : Math.floor((toGrams(capacityKg) * Math.max(0, pct)) / 100);

type At = (path: string, message: string) => void;

/**
 * Port of shared-cs `ContentValidator.KeyParts.cs` (`KeyPartSections`: siteKinds, keyParts, keyPartRules, items[].haul), S2-KEY. Same
 * errors, same wording, same order. The server's set-piece placement sweep for key-part and virtual-site rooms isn't ported (it needs
 * the interior generator); the server runs it on validate and publish, like the scenario sweep.
 */
export function validateKeyParts(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const categories = idsIn(b.categories);
  // The first item with an id wins, like the C# dictionary.
  const items = new Map<string, NonNullable<Bundle['items']>[number]>();
  for (const i of b.items ?? []) if (i != null && !items.has(i.id ?? '')) items.set(i.id ?? '', i);
  const chains = idsIn(b.beatChains);
  const scenarios = idsIn(b.scenarios);
  const setPieces = idsIn(b.setPieces);

  if (b.keyPartRules === null)
    err('keyPartRules', undefined, '', "Bundle is missing 'keyPartRules'.");
  const d = keyPartRulesDefaults();
  const rules = { ...d, ...(b.keyPartRules ?? {}) } as Required<KeyPartRulesDef>;
  const rat: At = (p, m) => err('keyPartRules', undefined, p, m);

  // Site kinds.
  const siteKinds = new Set<string>();
  (b.siteKinds ?? []).forEach((k, i) => {
    const at: At = (p, m) => err('siteKinds', i, p, m);
    if (k == null) {
      at('', 'siteKinds has an empty entry.');
      return;
    }
    const id = k.id ?? '';
    const where = `Site kind '${id}'`;
    if (!id.trim()) at('id', 'A site kind has no id.');
    else if (siteKinds.has(id)) at('id', `Duplicate site kind id '${id}'.`);
    else siteKinds.add(id);
    if (!k.osmTags || k.osmTags.length === 0)
      at('osmTags', `${where} needs at least one osmTags rule.`);
    else
      k.osmTags.forEach((tag, j) => {
        if (!isWellFormedTag(tag))
          at(
            `osmTags.${j}`,
            `${where} has a malformed osmTags rule '${tag ?? ''}' (key=value, key=*, joined by +).`,
          );
      });
    if (!categories.has(k.category ?? ''))
      at('category', `${where} uses unknown category '${k.category ?? ''}'.`);
  });

  // Virtual sites.
  const virtualSites = new Set<string>();
  (rules.virtualSites ?? []).forEach((v, i) => {
    const at: At = (p, m) => rat(`virtualSites.${i}${p ? `.${p}` : ''}`, m);
    if (v == null) {
      at('', 'keyPartRules.virtualSites has an empty entry.');
      return;
    }
    const id = v.id ?? '';
    const where = `Virtual site '${id}'`;
    if (!id.trim()) at('id', 'A virtual site has no id.');
    else if (virtualSites.has(id)) at('id', `Duplicate virtual site id '${id}'.`);
    else virtualSites.add(id);
    if (!(v.name ?? '').trim()) at('name', `${where} needs a name.`);
    if (!(v.line ?? '').trim()) at('line', `${where} needs a line.`);
    if (!categories.has(v.category ?? ''))
      at('category', `${where} uses unknown category '${v.category ?? ''}'.`);
    const rooms = v.roomCount ?? 3;
    if (rooms < 1 || rooms > MAX_ROOMS)
      at('roomCount', `${where} needs roomCount in 1..${MAX_ROOMS}.`);
    if (v.setPieceId && !setPieces.has(v.setPieceId))
      at('setPieceId', `${where} uses unknown set piece '${v.setPieceId}'.`);
  });

  // Rules.
  if (rules.hardLimitPct < 100 || rules.hardLimitPct > 400)
    rat('hardLimitPct', 'keyPartRules.hardLimitPct must be in 100..400.');
  if (!(rules.farRadiusM > 0)) rat('farRadiusM', 'keyPartRules farRadiusM must be above 0.');
  if (!(rules.siteTtlHours > 0)) rat('siteTtlHours', 'keyPartRules siteTtlHours must be above 0.');
  if (rules.rerollHours < 0) rat('rerollHours', 'keyPartRules rerollHours is negative.');
  if (rules.lostPieceRespawnHours < 0)
    rat('lostPieceRespawnHours', 'keyPartRules lostPieceRespawnHours is negative.');
  const haul = rules.haul === null ? d.haul : { ...d.haul, ...(rules.haul ?? {}) };
  haulClass(haul.heavy, 'heavy', rat);
  haulClass(haul.twoPerson, 'twoPerson', rat);
  haulClass(haul.drag, 'drag', rat);

  // Items with a haul block. The C# walks its id dictionary (first item per id), in insertion order.
  const itemIndex = new Map<string, number>();
  (b.items ?? []).forEach((i, n) => {
    if (i != null && !itemIndex.has(i.id ?? '')) itemIndex.set(i.id ?? '', n);
  });
  for (const [id, i] of items) {
    const at: At = (p, m) => err('items', itemIndex.get(id), p, m);
    if (i.haul != null) {
      if (!(HAUL_CLASSES as readonly string[]).includes(i.haul.class ?? ''))
        at('haul.class', `Item '${id}' has unknown haul class '${i.haul.class ?? ''}'.`);
      if (i.haul.fragile && i.haul.class !== 'pocket')
        at('haul.fragile', `Item '${id}' is fragile but not a pocket piece.`);
    }
    if (i.category === 'key_part' && i.haul == null)
      at('haul', `Key-part item '${id}' needs a haul block.`);
  }

  // Key parts.
  const fullTeam = fullTeamCapacityKg(b);
  const limit = hardLimitGrams(fullTeam, rules.hardLimitPct);
  const maxMembers =
    b.teamRules === null
      ? 1
      : (b.teamRules?.maxMembersPerTeam ?? teamRulesDefaults().maxMembersPerTeam ?? 1);
  const parts = new Set<string>();
  const partTiers = new Map<string, number>();
  (b.keyParts ?? []).forEach((p, n) => {
    const at: At = (path, m) => err('keyParts', n, path, m);
    if (p == null) {
      at('', 'keyParts has an empty entry.');
      return;
    }
    keyPart(p, at);
  });

  function keyPart(p: KeyPartDef, at: At) {
    const id = p.id ?? '';
    const tier = p.tier ?? 0;
    const where = `Key part '${id}'`;
    if (!id.trim()) at('id', 'A key part has no id.');
    else if (parts.has(id)) at('id', `Duplicate key part id '${id}'.`);
    else {
      parts.add(id);
      partTiers.set(id, tier);
    }
    if (tier < 2) at('tier', `${where} needs a tier of 2 or more (tier 1 has no gate).`);
    if (!(p.name ?? '').trim()) at('name', `${where} needs a name.`);
    if (!p.pieces || p.pieces.length === 0) at('pieces', `${where} needs at least one piece.`);
    (p.pieces ?? []).forEach((s, j) => {
      if (s == null) return;
      if ((s.quantity ?? 0) < 1)
        at(
          `pieces.${j}.quantity`,
          `${where} has a piece '${s.itemId ?? ''}' with quantity below 1.`,
        );
      const item = items.get(s.itemId ?? '');
      if (!item) {
        at(`pieces.${j}.itemId`, `${where} uses unknown piece item '${s.itemId ?? ''}'.`);
        return;
      }
      if (item.category !== 'key_part')
        at(`pieces.${j}.itemId`, `${where} piece '${item.id}' must be a key_part item.`);
      if (toGrams(Math.fround(item.weightKg ?? 0)) > limit)
        at(
          `pieces.${j}.itemId`,
          `${where} piece '${item.id}' weighs more than ${rules.hardLimitPct}% of a full team's pack (${fmtNum(fullTeam)} kg).`,
        );
      if (item.haul?.class === 'two_person' && maxMembers === 1)
        at(
          `pieces.${j}.itemId`,
          `${where} piece '${item.id}' needs two carriers, but teams hold one.`,
        );
    });
    if (!p.siteKinds || p.siteKinds.length === 0)
      at('siteKinds', `${where} needs at least one site kind.`);
    for (const k of p.siteKinds ?? [])
      if (!siteKinds.has(k ?? '')) at('siteKinds', `${where} uses unknown site kind '${k ?? ''}'.`);
    for (const c of p.fallbackCategories ?? [])
      if (!categories.has(c ?? ''))
        at('fallbackCategories', `${where} uses unknown fallback category '${c ?? ''}'.`);
    if (!p.virtualSiteIds || p.virtualSiteIds.length === 0)
      at('virtualSiteIds', `${where} needs at least one virtual site (the last fallback).`);
    for (const v of p.virtualSiteIds ?? [])
      if (!virtualSites.has(v ?? ''))
        at('virtualSiteIds', `${where} uses unknown virtual site '${v ?? ''}'.`);
    if (p.scenarioId && !scenarios.has(p.scenarioId))
      at('scenarioId', `${where} uses unknown scenario '${p.scenarioId}'.`);
    if (p.setPieceId && !setPieces.has(p.setPieceId))
      at('setPieceId', `${where} uses unknown set piece '${p.setPieceId}'.`);
    (p.beats ?? []).forEach((beat, j) => {
      if (beat == null) return;
      if (!chains.has(beat.beatId ?? ''))
        at(`beats.${j}.beatId`, `${where} uses unknown beat chain '${beat.beatId ?? ''}'.`);
      if (!isChance(beat.chance ?? 0))
        at(`beats.${j}.chance`, `${where} beat '${beat.beatId ?? ''}' needs a chance in 0..1.`);
    });
  }

  // Key-part items come only from a signed placement.
  const keyItems = new Set(
    [...items].filter(([, i]) => i.category === 'key_part').map(([id]) => id),
  );
  if (keyItems.size > 0) {
    (b.lootTables ?? []).forEach((t, n) => {
      for (const e of t?.entries ?? [])
        if (e != null && keyItems.has(e.itemId ?? ''))
          err(
            'lootTables',
            n,
            'entries',
            `Loot table '${t!.id ?? ''}' drops key-part item '${e.itemId}' (key parts are never loot).`,
          );
    });
    // A tier's blueprint rebuild recipes (techTiers[].rebuild, ADR 0039 §6) are the one way to make a key part.
    const rebuild = new Set(
      (b.techTiers ?? []).flatMap((t) => t?.rebuild ?? []).map((id) => id ?? ''),
    );
    (b.recipes ?? []).forEach((r, n) => {
      if (r != null && rebuild.has(r.id ?? '')) return;
      for (const s of r?.outputs ?? [])
        if (s != null && keyItems.has(s.itemId ?? ''))
          err(
            'recipes',
            n,
            'outputs',
            `Recipe '${r!.id ?? ''}' makes key-part item '${s.itemId}' (key parts are never crafted).`,
          );
    });
    (b.bosses ?? []).forEach((boss, n) => {
      for (const s of boss?.firstClearRewards ?? [])
        if (s != null && keyItems.has(s.itemId ?? ''))
          err(
            'bosses',
            n,
            'firstClearRewards',
            `Boss '${boss!.id ?? ''}' rewards key-part item '${s.itemId}'.`,
          );
    });
    (b.triggers ?? []).forEach((t, n) => {
      if (t == null || t.devOnly) return;
      for (const s of t.items?.stacks ?? [])
        if (s != null && keyItems.has(s.itemId ?? ''))
          err(
            'triggers',
            n,
            'items.stacks',
            `Trigger '${t.id ?? ''}' grants key-part item '${s.itemId}' (only dev triggers may).`,
          );
    });
    // A beat can hand over the placed piece but never create one (macguffin.html "Rule for beats"); scenario actions too.
    // give_items stacks and a trade's promised items, delays' `then` included (EffectRegistry.Flatten).
    const gives = (
      section: 'beatChains' | 'scenarios',
      n: number,
      path: string,
      where: string,
      effects: (EffectDef | null | undefined)[],
    ) => {
      for (const e of flatten(effects.filter((x): x is EffectDef => x != null))) {
        const given = [
          ...(e.type === 'give_items' ? (e.giveItems?.stacks ?? []) : []),
          ...(e.type === 'trade' ? (e.trade?.promised ?? []) : []),
        ];
        for (const s of given)
          if (s != null && keyItems.has(s.itemId ?? ''))
            err(
              section,
              n,
              path,
              `${where} gives key-part item '${s.itemId}' (a beat can hand over the placed piece, never create one).`,
            );
      }
    };
    (b.beatChains ?? []).forEach((c, n) => {
      if (c?.outcomes == null) return;
      for (const [id, o] of Object.entries(c.outcomes))
        gives(
          'beatChains',
          n,
          `outcomes.${id}.effects`,
          `Beat chain '${c.id ?? ''}' outcome '${id}'`,
          (o?.effects ?? []) as (EffectDef | null)[],
        );
    });
    (b.scenarios ?? []).forEach((s, n) => {
      if (s == null) return;
      const actions = [
        ...(s.triggers ?? []).flatMap((t) => t?.do ?? []),
        ...(s.onWin ?? []),
        ...(s.onLose ?? []),
      ];
      gives(
        'scenarios',
        n,
        '',
        `Scenario '${s.id ?? ''}'`,
        actions.map((a) => a?.effect as EffectDef | null | undefined),
      );
    });
    const kits: [string, { itemId?: string } | null | undefined][] = [
      ...((b.teamRules === undefined ? teamRulesDefaults() : b.teamRules)?.strangerKit ?? []).map(
        (s) => ['teamRules', s] as [string, typeof s],
      ),
      ...(b.survival?.starterCarried ?? []).map((s) => ['survival', s] as [string, typeof s]),
      ...(b.survival?.starterBase ?? []).map((s) => ['survival', s] as [string, typeof s]),
    ];
    for (const [section, s] of kits)
      if (s != null && keyItems.has(s.itemId ?? ''))
        err(
          section as 'teamRules' | 'survival',
          undefined,
          '',
          `A starter kit holds key-part item '${s.itemId}'.`,
        );
  }

  // Tech tiers name key parts of their own tier.
  (b.techTiers ?? []).forEach((t, n) => {
    if (t?.keyParts == null) return;
    for (const id of t.keyParts) {
      const tier = partTiers.get(id ?? '');
      if (tier === undefined)
        err(
          'techTiers',
          n,
          'keyParts',
          `Tech tier ${t.tier ?? 0} needs unknown key part '${id ?? ''}'.`,
        );
      else if (tier !== (t.tier ?? 0))
        err(
          'techTiers',
          n,
          'keyParts',
          `Tech tier ${t.tier ?? 0} needs key part '${id}', which is a tier ${tier} part.`,
        );
    }
  });
}

function haulClass(r: HaulClassRuleDef | null | undefined, name: string, at: At) {
  if (r == null) {
    at(`haul.${name}`, `keyPartRules.haul is missing '${name}'.`);
    return;
  }
  const speed = r.speed ?? 1;
  if (speed <= 0 || speed > 1)
    at(`haul.${name}.speed`, `keyPartRules.haul.${name}.speed must be in (0, 1].`);
  for (const f of ['pickupS', 'noiseM', 'everyS'] as const)
    if ((r[f] ?? 0) < 0) at(`haul.${name}.${f}`, `keyPartRules.haul.${name} ${f} is negative.`);
}
